/**
 * Who sees a virtual account (TMMAPP-0151 §F 18): the panel's visibility line from the served names, and
 * the served standing agreement.
 */
import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { meetingsApi } from '@/api/meetings';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';

/** Who a virtual account is visible to: what the visibility line reads. */
export interface Audience {
  mine: boolean;
  sharedWithNames: readonly string[];
  invitedNames: readonly string[];
  ownerDisplayName: string | null;
}

/**
 * Who sees it, from the virtual account's own details (TMMAPP-0162 item 3): for one the shell's list
 * doesn't carry (an archived one). The served list row stays the source when there is one. Names
 * only, as the list serves them: an ACTIVE share's display name, else "A member"; an invitation's,
 * else "Invited". Never an email.
 */
export function audienceFromDetail(detail: VirtualAccountDetail, t: TFunction): Audience {
  const named = (status: 'ACTIVE' | 'INVITED', fallback: string) =>
    detail.shares
      .filter((share) => share.status === status)
      .map((share) => {
        const name = share.displayName?.trim() ?? '';
        return name.length > 0 ? name : fallback;
      });
  return {
    mine: detail.mine,
    sharedWithNames: named('ACTIVE', t('people.aMember')),
    invitedNames: named('INVITED', t('people.invited')),
    ownerDisplayName: detail.ownerDisplayName,
  };
}

/** The visibility line (one whole sentence per case, so each locale orders it). */
export function visibilityLine(audience: Audience, t: TFunction, locale: string): string {
  if (!audience.mine) {
    return t('virtualAccounts.panel.visibleOwned', {
      owner: personName(audience.ownerDisplayName, t),
    });
  }
  const shared =
    audience.sharedWithNames.length === 0
      ? t('virtualAccounts.panel.visiblePrivate')
      : t('virtualAccounts.panel.visibleShared', {
          names: formatList(audience.sharedWithNames, locale),
        });
  return audience.invitedNames.length === 0
    ? shared
    : t('virtualAccounts.panel.withInvited', {
        line: shared,
        names: formatList(audience.invitedNames, locale),
      });
}

/**
 * The served standing agreement's body, or null — none written yet (a `404`), an empty one, or a
 * read that failed (the callout is left out; it never blocks the panel).
 */
export function useStandingAgreement(virtualAccountId: number | null): string | null {
  const [state, setState] = useState<{ id: number; body: string | null } | null>(null);
  useEffect(() => {
    if (virtualAccountId === null) return;
    let cancelled = false;
    meetingsApi
      .getVirtualAccountSummary(virtualAccountId)
      .then((summary) => {
        if (!cancelled) setState({ id: virtualAccountId, body: summary.body });
      })
      .catch(() => {
        if (!cancelled) setState({ id: virtualAccountId, body: null });
      });
    return () => {
      cancelled = true;
    };
  }, [virtualAccountId]);
  const body = state?.id === virtualAccountId ? state.body : null;
  return body !== null && body.trim().length > 0 ? body : null;
}
